import { useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, ComposedChart, Line, ReferenceLine
} from "recharts";

// ── Maghil Design Tokens ──
const T = {
  bg: "#F5F6FA", surface: "#FFFFFF", card: "#FFFFFF", cardBorder: "#E2E5EC",
  hover: "#F0F1F5", accent: "#D23A3B", accentSoft: "rgba(210,58,59,0.08)",
  accentGlow: "rgba(210,58,59,0.15)", green: "#16A34A", greenSoft: "rgba(22,163,74,0.08)",
  red: "#DC2626", redSoft: "rgba(220,38,38,0.08)", orange: "#D97706",
  orangeSoft: "rgba(217,119,6,0.08)", t1: "#1A1D2E", t2: "#5A6178", t3: "#9CA3B8",
  divider: "#E8EAF0", shadow: "0 1px 3px rgba(0,0,0,0.06)",
  font: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const MIcon = ({ name, size = 18, color = T.t3, fill = false, style: sx = {} }: any) => (
  <span className="material-symbols-outlined" style={{
    fontSize: size, color,
    fontVariationSettings: `'FILL' ${fill ? 1 : 0}, 'wght' 400, 'GRAD' 0, 'opsz' ${size > 20 ? 24 : 20}`,
    lineHeight: 1, userSelect: "none", ...sx,
  }}>{name}</span>
);

// ── Mock Data ──
const kpiRow1 = [
  { label: "TOTAL HOURS", value: "482.5", delta: "+8.2%", up: true, drillId: "hours" },
  { label: "SPLH", value: "$38.72", delta: "+5.1%", up: true, drillId: "splh" },
  { label: "TOTAL TIPS", value: "$6,842", delta: "+12.4%", up: true, drillId: "tips" },
];
const kpiRow2 = [
  { label: "ORDERS/HR", value: "4.8" },
  { label: "VOID RATE", value: "1.2%", drillId: "voids" },
  { label: "BREAK HRS", value: "66" },
];

const hourlyData = [
  { h: "6A", sales: 420, staff: 3 }, { h: "8A", sales: 1240, staff: 6 },
  { h: "10A", sales: 1320, staff: 7 }, { h: "12P", sales: 3680, staff: 12 },
  { h: "2P", sales: 2180, staff: 10 }, { h: "4P", sales: 1680, staff: 8 },
  { h: "6P", sales: 3520, staff: 12 }, { h: "8P", sales: 3240, staff: 12 },
  { h: "10P", sales: 1020, staff: 5 },
];
const splhTrend = [
  { d: "Mon", splh: 34.2 }, { d: "Tue", splh: 36.8 }, { d: "Wed", splh: 38.1 },
  { d: "Thu", splh: 41.2 }, { d: "Fri", splh: 44.6 }, { d: "Sat", splh: 42.3 }, { d: "Sun", splh: 38.7 },
];
const hoursData = [
  { d: "Mon", reg: 62, brk: 8 }, { d: "Tue", reg: 68, brk: 9 },
  { d: "Wed", reg: 72, brk: 10 }, { d: "Thu", reg: 74, brk: 10 },
  { d: "Fri", reg: 82, brk: 12 }, { d: "Sat", reg: 78, brk: 11 }, { d: "Sun", reg: 46, brk: 6 },
];
const voidReasons = [
  { name: "Customer Request", value: 42, color: T.accent }, { name: "Kitchen Error", value: 28, color: T.orange },
  { name: "Wrong Item", value: 18, color: T.red }, { name: "Quality Issue", value: 12, color: T.t3 },
];
const staffData = [
  { name: "Maria Santos", role: "Server", hours: 38.5, sales: 4820, tips: 682, orders: 96, splh: 125.19, status: "active", clockIn: "6:00 AM", clockOut: "2:30 PM", breakHrs: 1.0 },
  { name: "James Wilson", role: "Server", hours: 36.0, sales: 4210, tips: 594, orders: 88, splh: 116.94, status: "active", clockIn: "10:00 AM", clockOut: "6:00 PM", breakHrs: 1.0 },
  { name: "Ashley Chen", role: "Bartender", hours: 34.5, sales: 3980, tips: 720, orders: 142, splh: 115.36, status: "active", clockIn: "4:00 PM", clockOut: "12:30 AM", breakHrs: 0.5 },
  { name: "David Kim", role: "Server", hours: 40.0, sales: 3650, tips: 485, orders: 78, splh: 91.25, status: "break", clockIn: "8:00 AM", clockOut: "5:00 PM", breakHrs: 1.0 },
  { name: "Sarah Johnson", role: "Host", hours: 32.0, sales: 1240, tips: 180, orders: 45, splh: 38.75, status: "active", clockIn: "11:00 AM", clockOut: "7:00 PM", breakHrs: 1.0 },
  { name: "Carlos Rivera", role: "Server", hours: 28.5, sales: 3180, tips: 412, orders: 72, splh: 111.58, status: "active", clockIn: "5:00 PM", clockOut: "11:30 PM", breakHrs: 0.5 },
];
const voidLog = [
  { id: "V-1042", staff: "David Kim", item: "Wagyu Burger", amount: 34, reason: "Customer Request", time: "7:42 PM" },
  { id: "V-1041", staff: "James Wilson", item: "Truffle Fries", amount: 18, reason: "Kitchen Error", time: "6:18 PM" },
  { id: "V-1039", staff: "David Kim", item: "Caesar Salad", amount: 16, reason: "Wrong Item", time: "5:05 PM" },
  { id: "V-1038", staff: "Carlos Rivera", item: "Lobster Roll", amount: 28, reason: "Quality Issue", time: "1:22 PM" },
  { id: "V-1035", staff: "James Wilson", item: "Pasta Primavera", amount: 22, reason: "Kitchen Error", time: "12:40 PM" },
];

// ── Sub-Components ──
const DrillArrow = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" style={{ position: "absolute", top: 6, right: 6, opacity: 0.45 }}>
    <path d="M3.5 2L7 5L3.5 8" fill="none" stroke={T.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function KPICardDelta({ label, value, delta, up, drillId, onDrill }: any) {
  const hasDrill = !!drillId;
  const deltaColor = up ? T.green : T.red;
  const arrow = up ? "↑" : "↓";
  return (
    <div onClick={() => hasDrill && onDrill(drillId)} style={{
      flex: 1, background: T.card, borderRadius: 12, padding: "12px 10px", textAlign: "center",
      boxShadow: T.shadow, position: "relative", cursor: hasDrill ? "pointer" : "default",
      border: hasDrill ? `1px solid rgba(210,58,59,0.2)` : `1px solid ${T.cardBorder}`,
    }}>
      {hasDrill && <DrillArrow />}
      <div style={{ fontSize: 8, fontWeight: 400, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3, fontFamily: T.font }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: T.t1, marginTop: 4, fontFamily: T.font }}>{value}</div>
      <div style={{ fontSize: 9, fontWeight: 600, color: deltaColor, marginTop: 3, fontFamily: T.font }}>{arrow} {delta}</div>
    </div>
  );
}

function KPICardSimple({ label, value, drillId, onDrill }: any) {
  const hasDrill = !!drillId;
  return (
    <div onClick={() => hasDrill && onDrill(drillId)} style={{
      flex: 1, background: T.card, borderRadius: 10, padding: 10, textAlign: "center",
      boxShadow: T.shadow, position: "relative", cursor: hasDrill ? "pointer" : "default",
      border: hasDrill ? `1px solid rgba(210,58,59,0.2)` : `1px solid ${T.cardBorder}`,
    }}>
      {hasDrill && <DrillArrow />}
      <div style={{ fontSize: 8, fontWeight: 400, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3, fontFamily: T.font }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: T.t1, marginTop: 3, fontFamily: T.font }}>{value}</div>
    </div>
  );
}

function SectionCard({ title, subtitle, icon, children }: any) {
  return (
    <div style={{ background: T.card, borderRadius: 14, padding: 14, boxShadow: T.shadow, border: `1px solid ${T.cardBorder}`, marginBottom: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
        {icon && <MIcon name={icon} size={16} color={T.t2} />}
        <span style={{ fontSize: 12, fontWeight: 600, color: T.t1, fontFamily: T.font }}>{title}</span>
      </div>
      {subtitle && <div style={{ fontSize: 10, color: T.t3, fontFamily: T.font, marginTop: 1, marginBottom: 10, paddingLeft: icon ? 21 : 0 }}>{subtitle}</div>}
      {!subtitle && <div style={{ height: 10 }} />}
      {children}
    </div>
  );
}

function StaffCard({ staff, rank }: any) {
  const statusColor = staff.status === "active" ? T.green : T.orange;
  return (
    <div style={{ background: T.card, borderRadius: 10, padding: "10px 12px", boxShadow: T.shadow, border: `1px solid rgba(210,58,59,0.2)`, marginBottom: 5, position: "relative" }}>
      <DrillArrow />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 18, height: 18, borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 700, fontFamily: T.font, background: rank === 1 ? T.accentSoft : T.hover, color: rank === 1 ? T.accent : T.t3 }}>#{rank}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: statusColor }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: T.t1, fontFamily: T.font }}>{staff.name}</span>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.t1, fontFamily: T.font }}>${staff.sales.toLocaleString()}</div>
          <div style={{ fontSize: 8, color: T.t3, fontFamily: T.font }}>SALES</div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 5, marginTop: 8, paddingLeft: 26, flexWrap: "wrap" }}>
        {[
          { t: staff.role, bg: T.hover, c: T.t3 },
          { t: `${staff.hours}h`, bg: T.hover, c: T.t3 },
          { t: `$${staff.tips} tips`, bg: T.greenSoft, c: T.green },
          { t: staff.status === "active" ? "Active" : "Break", bg: staff.status === "active" ? T.greenSoft : T.orangeSoft, c: statusColor },
          { t: `$${staff.splh.toFixed(0)}/hr`, bg: T.hover, c: T.t3 },
        ].map((p, i) => <span key={i} style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, background: p.bg, color: p.c, fontFamily: T.font }}>{p.t}</span>)}
      </div>
    </div>
  );
}

function VoidLogEntry({ entry }: any) {
  return (
    <div style={{ background: T.card, borderRadius: 10, padding: "10px 12px", boxShadow: T.shadow, border: `1px solid ${T.cardBorder}`, marginBottom: 5 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: T.t1, fontFamily: T.font }}>{entry.id}</span>
            <span style={{ fontSize: 10, color: T.t3, fontFamily: T.font }}>{entry.time}</span>
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.accent, marginTop: 3, fontFamily: T.font }}>{entry.staff}</div>
          <div style={{ fontSize: 10, color: T.t2, marginTop: 2, fontFamily: T.font }}>{entry.item} — {entry.reason}</div>
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, color: T.orange, fontFamily: T.font }}>${entry.amount}</div>
      </div>
    </div>
  );
}

const MobileTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (<div style={{ background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 6, padding: "6px 10px", boxShadow: T.shadow, fontFamily: T.font }}>
    <div style={{ fontSize: 9, fontWeight: 600, color: T.t1 }}>{label}</div>
    {payload.map((p, i) => (<div key={i} style={{ fontSize: 9, color: T.t2 }}>{p.name}: <span style={{ fontWeight: 600, color: T.t1 }}>{typeof p.value === "number" && p.name !== "Staff" ? `$${p.value.toLocaleString()}` : p.value}</span></div>))}
  </div>);
};

// ── Drill Header ──
function DrillHeader({ title, breadcrumb, onBack }: any) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <div onClick={onBack} style={{ width: 28, height: 28, borderRadius: 7, background: T.hover, border: `1px solid ${T.divider}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
          <MIcon name="arrow_back" size={16} color={T.t3} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: T.t3, fontFamily: T.font }}>{breadcrumb}</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: T.t1, fontFamily: T.font }}>{title}</div>
        </div>
      </div>
    </div>
  );
}

function MiniKPI({ label, value, color }: any) {
  return (
    <div style={{ background: T.card, borderRadius: 10, padding: 10, textAlign: "center", boxShadow: T.shadow, border: `1px solid ${T.cardBorder}` }}>
      <div style={{ fontSize: 7, fontWeight: 400, color: T.t3, textTransform: "uppercase", fontFamily: T.font }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: color || T.t1, marginTop: 2, fontFamily: T.font }}>{value}</div>
    </div>
  );
}

// ── Drill Screens ──
function DrillHoursMobile({ onBack }: any) {
  return (<>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
      <MiniKPI label="TOTAL HRS" value="482.5" /><MiniKPI label="BREAK HRS" value="66.0" /><MiniKPI label="GROSS HRS" value="548.5" />
    </div>
    <SectionCard title="Daily Hours" subtitle="Regular + Breaks" icon="bar_chart">
      <ResponsiveContainer width="100%" height={80}>
        <BarChart data={hoursData} margin={{ top: 0, right: 2, left: -20, bottom: 0 }}>
          <XAxis dataKey="d" tick={{ fontSize: 7, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 7, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} width={28} />
          <Bar dataKey="reg" name="Regular" stackId="h" fill={T.accent} barSize={14} />
          <Bar dataKey="brk" name="Breaks" stackId="h" fill={T.t3} opacity={0.35} radius={[3, 3, 0, 0]} barSize={14} />
        </BarChart>
      </ResponsiveContainer>
    </SectionCard>
    {/* Staff timecard list */}
    <div style={{ fontSize: 12, fontWeight: 600, color: T.t1, fontFamily: T.font, marginBottom: 6, display: "flex", alignItems: "center", gap: 5 }}>
      <MIcon name="table_chart" size={16} color={T.t2} /> Staff Timecards
    </div>
    {staffData.map(s => (
      <div key={s.name} style={{ background: T.card, borderRadius: 10, padding: "10px 12px", boxShadow: T.shadow, border: `1px solid ${T.cardBorder}`, marginBottom: 5 }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.accent, fontFamily: T.font }}>{s.name}</div>
            <div style={{ fontSize: 10, color: T.t3, fontFamily: T.font }}>{s.role}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.t1, fontFamily: T.font }}>{s.hours.toFixed(1)}h</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 5, marginTop: 6, flexWrap: "wrap" }}>
          <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, background: T.hover, color: T.t3, fontFamily: T.font }}>In: {s.clockIn}</span>
          <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, background: T.hover, color: T.t3, fontFamily: T.font }}>Out: {s.clockOut}</span>
          <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, background: T.orangeSoft, color: T.orange, fontFamily: T.font }}>Break: {s.breakHrs}h</span>
        </div>
      </div>
    ))}
  </>);
}

function DrillSplhMobile({ onBack }: any) {
  const hourlyFull = [
    { h: "6A", sales: 420, staff: 3, splh: 140 }, { h: "8A", sales: 1240, staff: 6, splh: 207 },
    { h: "10A", sales: 1320, staff: 7, splh: 189 }, { h: "12P", sales: 3680, staff: 12, splh: 307 },
    { h: "2P", sales: 2180, staff: 10, splh: 218 }, { h: "4P", sales: 1680, staff: 8, splh: 210 },
    { h: "6P", sales: 3520, staff: 12, splh: 293 }, { h: "8P", sales: 3240, staff: 12, splh: 270 },
    { h: "10P", sales: 1020, staff: 5, splh: 204 },
  ];
  return (<>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
      <MiniKPI label="AVG SPLH" value="$38.72" /><MiniKPI label="PEAK" value="$44.60" color={T.green} /><MiniKPI label="LOW" value="$34.20" color={T.red} />
    </div>
    <SectionCard title="Hourly SPLH" subtitle="Sales ÷ Staff per hour" icon="stacked_line_chart">
      <ResponsiveContainer width="100%" height={100}>
        <ComposedChart data={hourlyFull} margin={{ top: 0, right: 2, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={T.divider} vertical={false} opacity={0.5} />
          <XAxis dataKey="h" tick={{ fontSize: 7, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} />
          <YAxis yAxisId="s" tick={{ fontSize: 7, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} width={28} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
          <YAxis yAxisId="sp" orientation="right" hide />
          <Tooltip content={<MobileTooltip />} />
          <Bar yAxisId="s" dataKey="sales" name="Sales" fill={T.accent} radius={[3, 3, 0, 0]} barSize={14} opacity={0.6} />
          <Line yAxisId="sp" type="monotone" dataKey="splh" name="SPLH" stroke={T.orange} strokeWidth={2} strokeDasharray="4 3" dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </SectionCard>
    <SectionCard title="Weekly SPLH Trend" subtitle="Target: $35/hr" icon="trending_up">
      <ResponsiveContainer width="100%" height={80}>
        <AreaChart data={splhTrend} margin={{ top: 0, right: 2, left: -20, bottom: 0 }}>
          <XAxis dataKey="d" tick={{ fontSize: 7, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} />
          <YAxis domain={[28, 50]} tick={{ fontSize: 7, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} width={28} tickFormatter={v => `$${v}`} />
          <ReferenceLine y={35} stroke={T.t3} strokeDasharray="4 3" strokeWidth={1} />
          <defs><linearGradient id="mSplhG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={T.accent} stopOpacity={0.15} /><stop offset="100%" stopColor={T.accent} stopOpacity={0.01} /></linearGradient></defs>
          <Area type="monotone" dataKey="splh" stroke={T.accent} strokeWidth={2} fill="url(#mSplhG)" dot={{ r: 2.5, fill: T.accent, stroke: "#fff", strokeWidth: 1.5 }} />
        </AreaChart>
      </ResponsiveContainer>
    </SectionCard>
  </>);
}

function DrillTipsMobile({ onBack }: any) {
  return (<>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
      <MiniKPI label="TOTAL TIPS" value="$6,842" /><MiniKPI label="TIP/HR" value="$14.18" /><MiniKPI label="TOP EARNER" value="Ashley C." />
    </div>
    <SectionCard title="Tip History" subtitle="Daily totals this week" icon="payments">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 4 }}>
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => {
          const vals = [820, 950, 980, 1100, 1280, 1020, 692];
          const max = 1280;
          return (<div key={d} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
            <div style={{ width: "100%", maxWidth: 18, height: 60, background: T.hover, borderRadius: 3, display: "flex", flexDirection: "column", justifyContent: "flex-end", overflow: "hidden" }}>
              <div style={{ height: `${(vals[i] / max) * 100}%`, background: T.green, borderRadius: "3px 3px 0 0", opacity: vals[i] === max ? 1 : 0.5 }} />
            </div>
            <span style={{ fontSize: 7, color: vals[i] === max ? T.t1 : T.t3, fontWeight: vals[i] === max ? 700 : 400, fontFamily: T.font }}>{d}</span>
          </div>);
        })}
      </div>
    </SectionCard>
    <div style={{ fontSize: 12, fontWeight: 600, color: T.t1, fontFamily: T.font, marginBottom: 6, display: "flex", alignItems: "center", gap: 5 }}><MIcon name="leaderboard" size={16} color={T.t2} /> Tips by Staff</div>
    {[...staffData].sort((a, b) => b.tips - a.tips).map(s => (
      <div key={s.name} style={{ background: T.card, borderRadius: 10, padding: "10px 12px", boxShadow: T.shadow, border: `1px solid ${T.cardBorder}`, marginBottom: 5 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div><div style={{ fontSize: 12, fontWeight: 600, color: T.accent, fontFamily: T.font }}>{s.name}</div><div style={{ fontSize: 10, color: T.t3, fontFamily: T.font }}>{s.role} · {s.hours}h</div></div>
          <div style={{ textAlign: "right" }}><div style={{ fontSize: 14, fontWeight: 700, color: T.green, fontFamily: T.font }}>${s.tips}</div><div style={{ fontSize: 9, color: T.t3, fontFamily: T.font }}>${(s.tips / s.hours).toFixed(2)}/hr</div></div>
        </div>
      </div>
    ))}
  </>);
}

function DrillVoidsMobile({ onBack }: any) {
  return (<>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
      <MiniKPI label="TOTAL VOIDS" value="$365" color={T.orange} /><MiniKPI label="VOID ITEMS" value="24" /><MiniKPI label="VOID RATE" value="1.2%" />
    </div>
    <SectionCard title="Reasons" subtitle="By category" icon="block">
      {voidReasons.map(r => { const total = voidReasons.reduce((s, v) => s + v.value, 0); const pct = ((r.value / total) * 100).toFixed(0);
        return (<div key={r.name} style={{ marginBottom: 6 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}><span style={{ fontSize: 11, color: T.t2, fontFamily: T.font }}>{r.name}</span><span style={{ fontSize: 11, fontWeight: 600, color: T.t1, fontFamily: T.font }}>{pct}%</span></div>
          <div style={{ height: 5, background: T.hover, borderRadius: 3, overflow: "hidden" }}><div style={{ height: "100%", width: `${pct}%`, background: r.color, borderRadius: 3, opacity: 0.65 }} /></div>
        </div>);
      })}
    </SectionCard>
    <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 6 }}><MIcon name="receipt_long" size={16} color={T.t2} /><span style={{ fontSize: 12, fontWeight: 600, color: T.t1, fontFamily: T.font }}>All Void Entries</span></div>
    {voidLog.map((e, i) => <VoidLogEntry key={`${e.id}-${i}`} entry={e} />)}
  </>);
}

// ── Main Dashboard ──
function MainDashboard({ onDrill }: any) {
  return (<>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
      {kpiRow1.map(k => <KPICardDelta key={k.label} {...k} onDrill={onDrill} />)}
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
      {kpiRow2.map(k => <KPICardSimple key={k.label} {...k} onDrill={onDrill} />)}
    </div>
    <SectionCard title="Labor vs. Sales" subtitle="Revenue bars · Staff line" icon="stacked_line_chart">
      <ResponsiveContainer width="100%" height={100}>
        <ComposedChart data={hourlyData} margin={{ top: 0, right: 2, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={T.divider} vertical={false} opacity={0.5} />
          <XAxis dataKey="h" tick={{ fontSize: 7, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} />
          <YAxis yAxisId="s" tick={{ fontSize: 7, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} width={28} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
          <YAxis yAxisId="st" orientation="right" hide />
          <Tooltip content={<MobileTooltip />} />
          <Bar yAxisId="s" dataKey="sales" name="Sales" fill={T.accent} radius={[3, 3, 0, 0]} barSize={14} opacity={0.7} />
          <Line yAxisId="st" type="monotone" dataKey="staff" name="Staff" stroke={T.green} strokeWidth={2} dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </SectionCard>
    <SectionCard title="SPLH Trend" subtitle="This week · Target $35/hr" icon="trending_up">
      <ResponsiveContainer width="100%" height={80}>
        <AreaChart data={splhTrend} margin={{ top: 0, right: 2, left: -20, bottom: 0 }}>
          <XAxis dataKey="d" tick={{ fontSize: 7, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} />
          <YAxis domain={[28, 50]} tick={{ fontSize: 7, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} width={28} tickFormatter={v => `$${v}`} />
          <ReferenceLine y={35} stroke={T.t3} strokeDasharray="4 3" strokeWidth={1} />
          <defs><linearGradient id="mSplhGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={T.accent} stopOpacity={0.15} /><stop offset="100%" stopColor={T.accent} stopOpacity={0.01} /></linearGradient></defs>
          <Area type="monotone" dataKey="splh" stroke={T.accent} strokeWidth={2} fill="url(#mSplhGrad)" dot={{ r: 2.5, fill: T.accent, stroke: "#fff", strokeWidth: 1.5 }} />
        </AreaChart>
      </ResponsiveContainer>
    </SectionCard>
    <SectionCard title="Daily Hours" subtitle="Regular + Breaks" icon="schedule">
      <ResponsiveContainer width="100%" height={80}>
        <BarChart data={hoursData} margin={{ top: 0, right: 2, left: -20, bottom: 0 }}>
          <XAxis dataKey="d" tick={{ fontSize: 7, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 7, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} width={28} />
          <Bar dataKey="reg" name="Regular" stackId="h" fill={T.accent} barSize={14} /><Bar dataKey="brk" name="Breaks" stackId="h" fill={T.t3} opacity={0.35} radius={[3, 3, 0, 0]} barSize={14} />
        </BarChart>
      </ResponsiveContainer>
      <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 8, color: T.t3 }}><div style={{ width: 6, height: 6, borderRadius: 1, background: T.accent }} /> Regular</div>
        <div style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 8, color: T.t3 }}><div style={{ width: 6, height: 6, borderRadius: 1, background: T.t3, opacity: 0.35 }} /> Breaks</div>
      </div>
    </SectionCard>
    {/* Staff Leaderboard */}
    <div style={{ background: T.card, borderRadius: 14, padding: 14, boxShadow: T.shadow, border: `1px solid ${T.cardBorder}`, marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}><MIcon name="leaderboard" size={16} color={T.t2} /><div><div style={{ fontSize: 12, fontWeight: 600, color: T.t1, fontFamily: T.font }}>Staff Leaderboard</div><div style={{ fontSize: 10, color: T.t3, fontFamily: T.font, marginTop: 1 }}>Ranked by sales</div></div></div>
      </div>
      {staffData.slice(0, 4).map((s, i) => <StaffCard key={s.name} staff={s} rank={i + 1} />)}
    </div>
    {/* Void Reasons */}
    <SectionCard title="Void Reasons" subtitle="This week" icon="block">
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {voidReasons.map(r => { const total = voidReasons.reduce((s, v) => s + v.value, 0); const pct = ((r.value / total) * 100).toFixed(0);
          return (<div key={r.name}><div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}><span style={{ fontSize: 11, color: T.t2, fontFamily: T.font }}>{r.name}</span><span style={{ fontSize: 11, fontWeight: 600, color: T.t1, fontFamily: T.font }}>{pct}%</span></div>
            <div style={{ height: 5, background: T.hover, borderRadius: 3, overflow: "hidden" }}><div style={{ height: "100%", width: `${pct}%`, background: r.color, borderRadius: 3, opacity: 0.65 }} /></div></div>);
        })}
      </div>
    </SectionCard>
    {/* Recent Voids */}
    <div style={{ background: T.card, borderRadius: 14, padding: 14, boxShadow: T.shadow, border: `1px solid ${T.cardBorder}`, marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}><MIcon name="receipt_long" size={16} color={T.t2} /><div><div style={{ fontSize: 12, fontWeight: 600, color: T.t1, fontFamily: T.font }}>Recent Voids</div><div style={{ fontSize: 10, color: T.t3, fontFamily: T.font, marginTop: 1 }}>Latest 3 entries</div></div></div>
        <div style={{ fontSize: 9, padding: "3px 8px", borderRadius: 4, background: T.accentSoft, color: T.accent, fontWeight: 500, fontFamily: T.font, cursor: "pointer", display: "flex", alignItems: "center", gap: 3 }} onClick={() => onDrill("voids")}>View All <MIcon name="arrow_forward" size={12} color={T.accent} /></div>
      </div>
      {voidLog.slice(0, 3).map(e => <VoidLogEntry key={e.id} entry={e} />)}
    </div>
    {/* Alert */}
    <div style={{ background: T.redSoft, border: `1px solid rgba(220,38,38,0.2)`, borderRadius: 12, padding: "12px 14px", marginBottom: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}><MIcon name="warning" size={16} color={T.red} fill /><span style={{ fontSize: 11, fontWeight: 600, color: T.red, fontFamily: T.font }}>Overtime Risk</span></div>
      <div style={{ fontSize: 11, color: T.t2, lineHeight: 1.5, fontFamily: T.font }}><span style={{ fontWeight: 700, color: T.t1 }}>David Kim</span> has logged <span style={{ fontWeight: 700, color: T.t1 }}>40.0 hrs</span> this week — at the OT threshold.</div>
    </div>
  </>);
}

// ── Root Component ──
export default function LaborReportMobile() {
  const [screen, setScreen] = useState("main"); // main | hours | splh | tips | voids
  const [activeTab, setActiveTab] = useState(1);
  const [sourceFilter, setSourceFilter] = useState("All");

  const drillTitles = { hours: "Total Hours", splh: "SPLH Detail", tips: "Tip Breakdown", voids: "Void Log" };
  const drillBreadcrumbs = { hours: "Labor > Hours", splh: "Labor > SPLH", tips: "Labor > Tips", voids: "Labor > Voids" };

  const bottomTabs = [
    { label: "Snapshot", icon: "bolt" }, { label: "Reports", icon: "bar_chart" },
    { label: "Maghil AI", icon: "auto_awesome" }, { label: "Support", icon: "chat_bubble_outline" },
  ];

  return (
    <div style={{ width: 375, height: 812, margin: "0 auto", background: T.bg, fontFamily: T.font, display: "flex", flexDirection: "column", overflow: "hidden", borderRadius: 20, boxShadow: "0 4px 24px rgba(0,0,0,0.12)", position: "relative" }}>
      <link href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200" rel="stylesheet" />
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

      {/* Status Bar */}
      <div style={{ height: 44, background: T.surface, display: "flex", alignItems: "flex-end", justifyContent: "center", padding: "0 20px 4px" }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: T.t1, fontFamily: T.font }}>9:41</div>
      </div>

      {/* Header */}
      {screen === "main" ? (
        <div style={{ padding: "8px 20px", background: T.surface }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: T.t1, fontFamily: T.font }}>Labor</div>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: T.accentSoft, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: T.accent, fontFamily: T.font }}>AT</div>
          </div>
          {/* Outlet + Source filter row */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px", background: T.surface, border: `1px solid ${T.divider}`, borderRadius: 8, fontSize: 11, fontWeight: 600, color: T.t1, fontFamily: T.font }}>
              <MIcon name="location_on" size={14} color={T.accent} fill /> Downtown Austin <MIcon name="expand_more" size={14} color={T.t3} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 2, padding: 2, background: T.bg, borderRadius: 6 }}>
              {["All", "Maghil", "3rd Party"].map(s => (
                <button key={s} onClick={() => setSourceFilter(s)} style={{
                  padding: "3px 8px", borderRadius: 5, border: "none", cursor: "pointer",
                  fontSize: 9.5, fontWeight: sourceFilter === s ? 600 : 400, fontFamily: T.font,
                  background: sourceFilter === s ? T.surface : "transparent",
                  color: sourceFilter === s ? T.accent : T.t3,
                  boxShadow: sourceFilter === s ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
                  transition: "all 0.15s",
                }}>{s}</button>
              ))}
            </div>
          </div>
          {/* Date Picker */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 4 }}>
            <div style={{ width: 26, height: 26, borderRadius: 7, background: T.hover, border: `1px solid ${T.divider}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}><MIcon name="chevron_left" size={16} color={T.t3} /></div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 12px", background: T.surface, border: `1px solid ${T.divider}`, borderRadius: 8 }}>
              <MIcon name="calendar_today" size={14} color={T.t3} />
              <div><div style={{ fontSize: 12, fontWeight: 600, color: T.t1, fontFamily: T.font }}>This Week</div><div style={{ fontSize: 11, color: T.t3, fontFamily: T.font }}>Mar 3 – Mar 9, 2026</div></div>
            </div>
            <div style={{ width: 26, height: 26, borderRadius: 7, background: T.hover, border: `1px solid ${T.divider}`, display: "flex", alignItems: "center", justifyContent: "center", opacity: 0.3 }}><MIcon name="chevron_right" size={16} color={T.t3} /></div>
          </div>
        </div>
      ) : (
        <div style={{ padding: "4px 16px", background: T.surface }}>
          <DrillHeader title={drillTitles[screen]} breadcrumb={drillBreadcrumbs[screen]} onBack={() => setScreen("main")} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 12px", background: T.surface, border: `1px solid ${T.divider}`, borderRadius: 8 }}>
              <MIcon name="calendar_today" size={14} color={T.t3} />
              <div><div style={{ fontSize: 12, fontWeight: 600, color: T.t1, fontFamily: T.font }}>This Week</div><div style={{ fontSize: 11, color: T.t3, fontFamily: T.font }}>Mar 3 – Mar 9</div></div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div style={{ flex: 1, overflowY: "auto", padding: "8px 16px 16px", WebkitOverflowScrolling: "touch" }}>
        {screen === "main" && <MainDashboard onDrill={setScreen} />}
        {screen === "hours" && <DrillHoursMobile onBack={() => setScreen("main")} />}
        {screen === "splh" && <DrillSplhMobile onBack={() => setScreen("main")} />}
        {screen === "tips" && <DrillTipsMobile onBack={() => setScreen("main")} />}
        {screen === "voids" && <DrillVoidsMobile onBack={() => setScreen("main")} />}
      </div>

      {/* Bottom Nav */}
      <div style={{ height: 72, background: T.surface, borderTop: `1px solid ${T.divider}`, display: "flex", alignItems: "flex-start", justifyContent: "space-around", paddingTop: 8, paddingBottom: 8 }}>
        {bottomTabs.map((tab, i) => {
          const isActive = i === activeTab;
          return (<div key={tab.label} onClick={() => setActiveTab(i)} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "4px 14px", cursor: "pointer" }}>
            <MIcon name={tab.icon} size={22} color={isActive ? T.accent : T.t3} fill={isActive} style={{ opacity: isActive ? 1 : 0.5 }} />
            <span style={{ fontSize: 9.5, fontWeight: isActive ? 700 : 400, color: isActive ? T.accent : T.t3, fontFamily: T.font }}>{tab.label}</span>
            {isActive && <div style={{ width: 4, height: 4, borderRadius: "50%", background: T.accent, boxShadow: `0 0 6px ${T.accentGlow}`, marginTop: 1 }} />}
          </div>);
        })}
      </div>
    </div>
  );
}
